/* eslint-disable @typescript-eslint/no-unused-vars */
import { TreeNode } from 'src/components/Tree/type';
import i18nInst from 'src/i18n';
import {
  ActionData,
  ActionEnum,
  ExchangeOnlinePolicyItem,
  VsActionKeys,
  VsNotifyTypeKeys,
  VsSuspiciousTagKeys,
} from 'src/pages/Policy/utils/types';

import { PolicyTypeEnum } from '@Policy/utils/constants';
import { PolicySubTypeEnum } from '@Types/index';
import { PolicyStore } from '@Policy/store';
import { UserIcon } from '@tonic-one/react-icons';

export type ActionConfig = Pick<
  ActionData,
  | 'threatType'
  | 'action'
  | 'notifyType'
  | 'suspiciousTag'
  | 'actions'
  | 'actionKey'
  | 'notifyTypeKey'
  | 'suspiciousTagKey'
>;
export const vsSpecifiedFileTypeSelector = (s: PolicyStore) => {
  const fileTypes = s.config.fileTypes;
  const template = s.template as ExchangeOnlinePolicyItem;
  const vsSpecifiedFileTypeObjects =
    template.tmVirusscans.vsSpecifiedFileTypeObjects;
  const selectedKeys: string[] = vsSpecifiedFileTypeObjects
    .split('|')
    .filter((type: string) => type.length > 0);
  const predefinedTypes = fileTypes[0].children;
  let isAddTotalKey = true;
  predefinedTypes.forEach((singleType: any) => {
    if (
      singleType.children.every((type: any) => selectedKeys.includes(type.key))
    ) {
      selectedKeys.push(singleType.key);
    } else {
      isAddTotalKey = false;
    }
  });
  if (isAddTotalKey) {
    selectedKeys.push(fileTypes[0].key);
  }
  return selectedKeys;
};

export const vsActionDataSelector = (s: PolicyStore) => {
  const { t } = i18nInst;
  const template = s.template as ExchangeOnlinePolicyItem;
  const {
    setVsState,
    config: {
      featureShow: { gmailQtSettingFeatureShow },
    },
  } = s;
  const {
    tmVirusscans: {
      vsDisarmActionType,
      vsDisarmNotifyType,
      vsVirusesActionType,
      vsVirusesNotifyType,
      vsWormsTrojansActionType,
      vsWormsTrojansNotifyType,
      vsPackersActionType,
      vsPackersNotifyType,
      vsMaliciousActionType,
      vsMaliciousNotifyType,
      vsAdditionalThreatsActionType,
      vsAdditionalThreatsNotifyType,
      vsRansomwareActionType,
      vsRansomwareNotifyType,
      vsOverRestrictionActionType,
      vsOverRestrictionNotifyType,
      vsOverFilesizeRestrictionActionType,
      vsOverFilesizeRestrictionNotifyType,
    },
  } = template;
  const policyType = s.template.policyType;
  let actions: ActionEnum[] = [];
  let actionOptionsUscannable: ActionEnum[] = [];
  let actionOptionsOversize: ActionEnum[] = [];
  let overSizeActionStr: string = t('policy.threatType.overFileSizeRestrict', {
    size: 150,
  });
  // Exchange ExchangeInline
  const actionOptionsExchange = [
    ActionEnum.quarantine,
    ActionEnum.delete,
    ActionEnum.pass,
    ActionEnum.replaceWithTextFile,
  ];
  const actionOptionsUscannableExchange: ActionEnum[] = [
    ActionEnum.pass,
    ActionEnum.quarantine,
    ActionEnum.delete,
    ActionEnum.replaceWithTextFile,
    ActionEnum.passWithoutLogging,
  ];
  const actionOptionsOversizeExchange: ActionEnum[] = [
    ActionEnum.pass,
    ActionEnum.quarantine,
    ActionEnum.delete,
    ActionEnum.replaceWithTextFile,
    ActionEnum.passWithoutLogging,
  ];
  // Gmail
  const actionOptionsGmail = gmailQtSettingFeatureShow
    ? [
        ActionEnum.delete,
        ActionEnum.pass,
        ActionEnum.quarantine,
        ActionEnum.labelEmail,
      ]
    : [ActionEnum.delete, ActionEnum.pass, ActionEnum.labelEmail];
  const actionOptionsUscannableGmail = gmailQtSettingFeatureShow
    ? [
        ActionEnum.pass,
        ActionEnum.quarantine,
        ActionEnum.delete,
        ActionEnum.labelEmail,
        ActionEnum.passWithoutLogging,
      ]
    : [
        ActionEnum.pass,
        ActionEnum.delete,
        ActionEnum.labelEmail,
        ActionEnum.passWithoutLogging,
      ];
  const actionOptionsOversizeGmail = gmailQtSettingFeatureShow
    ? [
        ActionEnum.pass,
        ActionEnum.quarantine,
        ActionEnum.delete,
        ActionEnum.labelEmail,
        ActionEnum.passWithoutLogging,
      ]
    : [
        ActionEnum.pass,
        ActionEnum.delete,
        ActionEnum.labelEmail,
        ActionEnum.passWithoutLogging,
      ];
  // Gmail inline
  const actionOptionsGmailInline = [
    ActionEnum.delete,
    ActionEnum.pass,
    ActionEnum.replaceWithTextFile,
    ActionEnum.quarantine,
  ];
  const actionOptionsUscannableGmailInline = [
    ActionEnum.pass,
    ActionEnum.quarantine,
    ActionEnum.delete,
    ActionEnum.passWithoutLogging,
  ];
  const actionOptionsOversizeGmailInline = [
    ActionEnum.pass,
    ActionEnum.quarantine,
    ActionEnum.delete,
    ActionEnum.passWithoutLogging,
  ];
  // Teams, OneDrive, SharePoint, Box, GoogleDrive, Dopbox
  const actionOptionsTeams = [
    ActionEnum.quarantine,
    ActionEnum.delete,
    ActionEnum.pass,
  ];
  const actionOptionsUscannableTeams = [
    ActionEnum.pass,
    ActionEnum.quarantine,
    ActionEnum.delete,
    ActionEnum.passWithoutLogging,
  ];

  const actionOptionsOversizeTeams = [
    ActionEnum.pass,
    ActionEnum.quarantine,
    ActionEnum.delete,
    ActionEnum.passWithoutLogging,
  ];
  // Teams chat
  const actionOptionsTeamsChat = [ActionEnum.pass, ActionEnum.block];
  const actionOptionsUscannableTeamsChat = [
    ActionEnum.pass,
    ActionEnum.block,
    ActionEnum.passWithoutLogging,
  ];

  const actionOptionsOversizeTeamsChat = [
    ActionEnum.pass,
    ActionEnum.block,
    ActionEnum.passWithoutLogging,
  ];
  // Salesforce
  const actionOptionsSalesforce = [
    ActionEnum.quarantine,
    ActionEnum.delete,
    ActionEnum.pass,
    ActionEnum.tagFileName,
  ];
  const actionOptionsUscannableSalesforce = [
    ActionEnum.pass,
    ActionEnum.quarantine,
    ActionEnum.delete,
    ActionEnum.tagFileName,
    ActionEnum.passWithoutLogging,
  ];
  const actionOptionsOversizeSalesforce = [
    ActionEnum.pass,
    ActionEnum.quarantine,
    ActionEnum.delete,
    ActionEnum.tagFileName,
    ActionEnum.passWithoutLogging,
  ];
  switch (policyType) {
    case PolicyTypeEnum.Exchange:
    case PolicyTypeEnum.ExchangeInline:
      actions = actionOptionsExchange;
      actionOptionsUscannable = actionOptionsUscannableExchange;
      actionOptionsOversize = actionOptionsOversizeExchange;
      break;
    case PolicyTypeEnum.Gmail:
      actions = actionOptionsGmail;
      actionOptionsUscannable = actionOptionsUscannableGmail;
      actionOptionsOversize = actionOptionsOversizeGmail;
      break;
    case PolicyTypeEnum.GmailInline:
      actions = actionOptionsGmailInline;
      actionOptionsUscannable = actionOptionsUscannableGmailInline;
      actionOptionsOversize = actionOptionsOversizeGmailInline;
      break;
    case PolicyTypeEnum.Teams:
    case PolicyTypeEnum.Box:
    case PolicyTypeEnum.GoogleDrive:
    case PolicyTypeEnum.OneDrive:
    case PolicyTypeEnum.SharePoint:
    case PolicyTypeEnum.Dropbox:
      actions = actionOptionsTeams;
      actionOptionsUscannable = actionOptionsUscannableTeams;
      actionOptionsOversize = actionOptionsOversizeTeams;
      break;
    case PolicyTypeEnum.SalesforceSandbox:
    case PolicyTypeEnum.SalesforceProduction:
      actions = actionOptionsSalesforce;
      actionOptionsUscannable = actionOptionsUscannableSalesforce;
      actionOptionsOversize = actionOptionsOversizeSalesforce;
      break;
    case PolicyTypeEnum.TeamsChat:
      actions = actionOptionsTeamsChat;
      actionOptionsUscannable = actionOptionsUscannableTeamsChat;
      actionOptionsOversize = actionOptionsOversizeTeamsChat;
      break;
    default:
      break;
  }

  const activeContentActions = [
    ActionEnum.sanitizeFile,
    ActionEnum.pass,
    ActionEnum.quarantine,
    ActionEnum.delete,
  ];
  // over size action string
  // email services use the default one
  // collaboration services
  if (
    [
      PolicyTypeEnum.Teams,
      PolicyTypeEnum.TeamsChat,
      PolicyTypeEnum.OneDrive,
      PolicyTypeEnum.SharePoint,
      PolicyTypeEnum.GoogleDrive,
    ].includes(policyType)
  ) {
    overSizeActionStr = t('policy.threatType.overFileSizeRestrict4Teams', {
      size: 500,
    });
  } else if (
    [PolicyTypeEnum.Dropbox, PolicyTypeEnum.Box].includes(policyType)
  ) {
    overSizeActionStr = t('policy.threatType.overFileSizeRestrict4Teams', {
      size: 150,
    });
  }
  let actionData: ActionData[] = [
    {
      threatType: t('policy.threatType.activeContent'),
      handler: setVsState,
      action: vsDisarmActionType,
      actionKey: VsActionKeys.vsDisarmActionType,
      actions: activeContentActions,
      notifyType: vsDisarmNotifyType,
      notifyTypeKey: VsNotifyTypeKeys.vsDisarmNotifyType,
    },
    {
      threatType: t('policy.threatType.virus'),
      handler: setVsState,
      action: vsVirusesActionType,
      actionKey: VsActionKeys.vsVirusesActionType,
      notifyType: vsVirusesNotifyType,
      notifyTypeKey: VsNotifyTypeKeys.vsVirusesNotifyType,
      actions,
    },
    {
      threatType: t('policy.threatType.worms'),
      handler: setVsState,
      action: vsWormsTrojansActionType,
      actionKey: VsActionKeys.vsWormsTrojansActionType,
      notifyType: vsWormsTrojansNotifyType,
      notifyTypeKey: VsNotifyTypeKeys.vsWormsTrojansNotifyType,
      actions,
    },
    {
      threatType: t('policy.threatType.packedFiles'),
      handler: setVsState,
      action: vsPackersActionType,
      actionKey: VsActionKeys.vsPackersActionType,
      notifyType: vsPackersNotifyType,
      notifyTypeKey: VsNotifyTypeKeys.vsPackersNotifyType,
      actions,
    },
    {
      threatType: t('policy.threatType.malciousCode'),
      handler: setVsState,
      action: vsMaliciousActionType,
      actionKey: VsActionKeys.vsMaliciousActionType,
      notifyType: vsMaliciousNotifyType,
      notifyTypeKey: VsNotifyTypeKeys.vsMaliciousNotifyType,
      actions,
    },
    {
      threatType: t('policy.threatType.spyware'),
      handler: setVsState,
      action: vsAdditionalThreatsActionType,
      actionKey: VsActionKeys.vsAdditionalThreatsActionType,
      notifyType: vsAdditionalThreatsNotifyType,
      notifyTypeKey: VsNotifyTypeKeys.vsAdditionalThreatsNotifyType,
      actions,
    },
    {
      threatType: t('policy.threatType.ransomware'),
      handler: setVsState,
      action: vsRansomwareActionType,
      actionKey: VsActionKeys.vsRansomwareActionType,
      notifyType: vsRansomwareNotifyType,
      notifyTypeKey: VsNotifyTypeKeys.vsRansomwareNotifyType,
      actions,
    },
    {
      threatType: t('policy.threatType.unscannableFiles'),
      handler: setVsState,
      action: vsOverRestrictionActionType,
      actionKey: VsActionKeys.vsOverRestrictionActionType,
      notifyType: vsOverRestrictionNotifyType,
      notifyTypeKey: VsNotifyTypeKeys.vsOverRestrictionNotifyType,
      actions: actionOptionsUscannable,
    },
    {
      threatType: overSizeActionStr,
      handler: setVsState,
      action: vsOverFilesizeRestrictionActionType,
      actionKey: VsActionKeys.vsOverFilesizeRestrictionActionType,
      notifyType: vsOverFilesizeRestrictionNotifyType,
      notifyTypeKey: VsNotifyTypeKeys.vsOverFilesizeRestrictionNotifyType,
      actions: actionOptionsOversize,
    },
  ];
  if (
    ![PolicyTypeEnum.Exchange, PolicyTypeEnum.ExchangeInline].includes(
      policyType
    )
  ) {
    actionData = actionData.filter(
      (item) => item.actionKey !== VsActionKeys.vsDisarmActionType
    );
  }
  return actionData;
};

export const vsPswProtectedFileActionSelector = (s: PolicyStore) => {
  const {
    tmVirusscans: {
      vsEncryptedCompressedFileActionType,
      vsEncryptedCompressedFileNotifyType,
      vsOtherEncryptedFileActionType,
      vsOtherEncryptedFileNotifyType,
      vsTextTagSubjectContent,
    },
  } = s.template as ExchangeOnlinePolicyItem;
  const {
    config: {
      featureShow: { gmailQtSettingFeatureShow, passwordQuarantineFeatureShow },
    },
  } = s;
  const { t } = i18nInst;
  const policyType = s.template.policyType;
  const policySubType = s.template.policySubType;
  let actionOptionsPwsProtectedFile: ActionEnum[] = [];
  let actionOptionsOtherPwsProtectedFile: ActionEnum[] = [];
  const isExchangeInlineInbound =
    policyType === PolicyTypeEnum.ExchangeInline &&
    policySubType === PolicySubTypeEnum.inbound;
  // password rescan runs on Exchange API mode and Exchange inline inbound
  const showPasswordQuarantine =
    !!passwordQuarantineFeatureShow &&
    (policyType === PolicyTypeEnum.Exchange || isExchangeInlineInbound);
  // Exchange Exchange inline
  const actionOptionsPwsProtectedFileExchange = [
    ActionEnum.quarantine,
    ...(showPasswordQuarantine ? [ActionEnum.passwordQuarantine] : []),
    ActionEnum.delete,
    ActionEnum.pass,
    ActionEnum.replaceWithTextFile,
    ActionEnum.passWithoutLogging,
  ];
  const actionOptionsOtherPwsProtectedFileExchange = [
    ActionEnum.quarantine,
    ...(showPasswordQuarantine ? [ActionEnum.passwordQuarantine] : []),
    ActionEnum.delete,
    ActionEnum.pass,
    ActionEnum.replaceWithTextFile,
    ActionEnum.tagSubject,
    ActionEnum.passWithoutLogging,
  ];
  // Teams OneDrive SharePoint GmailInline Box, GoogleDrive, Dropbox
  const actionOptionsPwsProtectedFileTeams = [
    ActionEnum.quarantine,
    ActionEnum.delete,
    ActionEnum.pass,
    ActionEnum.passWithoutLogging,
  ];
  const actionOptionsOtherPwsProtectedFileTeams =
    actionOptionsPwsProtectedFileTeams;
  // Teams chat
  const actionOptionsPwsProtectedFileTeamsChat = [
    ActionEnum.pass,
    ActionEnum.block,
    ActionEnum.passWithoutLogging,
  ];
  const actionOptionsOtherPwsProtectedFileTeamsChat =
    actionOptionsPwsProtectedFileTeamsChat;
  // Gmail
  const actionOptionsPwsProtectedFileGmail = gmailQtSettingFeatureShow
    ? [
        ActionEnum.delete,
        ActionEnum.pass,
        ActionEnum.quarantine,
        ActionEnum.labelEmail,
        ActionEnum.passWithoutLogging,
      ]
    : [
        ActionEnum.delete,
        ActionEnum.pass,
        ActionEnum.labelEmail,
        ActionEnum.passWithoutLogging,
      ];
  const actionOptionsOtherPwsProtectedFileGmail =
    actionOptionsPwsProtectedFileGmail;
  // Salesforce
  const actionOptionsPwsProtectedFileSalesforce = [
    ActionEnum.quarantine,
    ActionEnum.delete,
    ActionEnum.pass,
    ActionEnum.tagFileName,
    ActionEnum.passWithoutLogging,
  ];
  const actionOptionsOtherPwsProtectedFileSalesforce = [
    ActionEnum.quarantine,
    ActionEnum.delete,
    ActionEnum.pass,
    ActionEnum.tagFileName,
    ActionEnum.passWithoutLogging,
  ];
  switch (policyType) {
    case PolicyTypeEnum.Exchange:
    case PolicyTypeEnum.ExchangeInline:
      actionOptionsPwsProtectedFile = actionOptionsPwsProtectedFileExchange;
      actionOptionsOtherPwsProtectedFile =
        actionOptionsOtherPwsProtectedFileExchange;
      break;
    case PolicyTypeEnum.Teams:
    case PolicyTypeEnum.OneDrive:
    case PolicyTypeEnum.SharePoint:
    case PolicyTypeEnum.GmailInline:
    case PolicyTypeEnum.Box:
    case PolicyTypeEnum.Dropbox:
    case PolicyTypeEnum.GoogleDrive:
      actionOptionsPwsProtectedFile = actionOptionsPwsProtectedFileTeams;
      actionOptionsOtherPwsProtectedFile =
        actionOptionsOtherPwsProtectedFileTeams;
      break;
    case PolicyTypeEnum.SalesforceSandbox:
    case PolicyTypeEnum.SalesforceProduction:
      actionOptionsPwsProtectedFile = actionOptionsPwsProtectedFileSalesforce;
      actionOptionsOtherPwsProtectedFile =
        actionOptionsOtherPwsProtectedFileSalesforce;
      break;
    case PolicyTypeEnum.TeamsChat:
      actionOptionsPwsProtectedFile = actionOptionsPwsProtectedFileTeamsChat;
      actionOptionsOtherPwsProtectedFile =
        actionOptionsOtherPwsProtectedFileTeamsChat;
      break;
    case PolicyTypeEnum.Gmail:
      actionOptionsPwsProtectedFile = actionOptionsPwsProtectedFileGmail;
      actionOptionsOtherPwsProtectedFile =
        actionOptionsOtherPwsProtectedFileGmail;
      break;
    default:
      break;
  }
  const actionData: ActionConfig[] = [
    {
      threatType: t('policy.threatType.passwordProtected'),
      action: vsEncryptedCompressedFileActionType,
      actionKey: VsActionKeys.vsEncryptedCompressedFileActionType,
      notifyType: vsEncryptedCompressedFileNotifyType,
      notifyTypeKey: VsNotifyTypeKeys.vsEncryptedCompressedFileNotifyType,
      actions: actionOptionsPwsProtectedFile,
    },
    {
      threatType: t('policy.threatType.otherPasswordProtected'),
      action: vsOtherEncryptedFileActionType,
      actionKey: VsActionKeys.vsOtherEncryptedFileActionType,
      notifyType: vsOtherEncryptedFileNotifyType,
      notifyTypeKey: VsNotifyTypeKeys.vsOtherEncryptedFileNotifyType,
      suspiciousTag: vsTextTagSubjectContent,
      suspiciousTagKey: VsSuspiciousTagKeys.vsTextTagSubjectContent,
      actions: actionOptionsOtherPwsProtectedFile,
    },
  ];
  return actionData;
};

export const vsSpecifiedFileExtObjectsSelector = (s: PolicyStore) => {
  const {
    tmVirusscans: { vsSpecifiedFileExtObjects },
  } = s.template as ExchangeOnlinePolicyItem;
  const separator = '|';
  const list = vsSpecifiedFileExtObjects.split(separator).sort();
  const displayList: { name: string; value: string }[] = [];
  list.forEach((item) => {
    if (item !== '') {
      // react will treat all the variant in jsx as a string, no need to do char2entity process
      displayList.push({ name: item, value: item });
    }
  });
  return displayList;
};

export const vsDisarmExceptSenderStringSelector = (s: PolicyStore) => {
  const {
    tmVirusscans: { vsDisarmExceptSenderString },
  } = s.template as ExchangeOnlinePolicyItem;
  const separator = ';';
  const list = vsDisarmExceptSenderString
    .split(separator)
    .filter((item: string) => item.length > 0);
  const displayList: TreeNode[] = list.map((item: string) => ({
    title: item,
    key: item,
    icon: { name: 'user', component: <UserIcon /> },
  }));
  return displayList;
};
export const vsDisarmExceptReceiverStringSelector = (s: PolicyStore) => {
  const {
    tmVirusscans: { vsDisarmExceptReceiverString },
  } = s.template as ExchangeOnlinePolicyItem;
  const separator = ';';
  const list = vsDisarmExceptReceiverString
    .split(separator)
    .filter((item: string) => item.length > 0);
  const displayList: TreeNode[] = list.map((item: string) => ({
    title: item,
    key: item,
    icon: { name: 'user', component: <UserIcon /> },
  }));
  return displayList;
};
function convertTarget(target: TreeNode): TreeNode {
  const { icon, key } = target;
  if (icon?.name === 'user') {
    return {
      ...target,
      disabled: false,
    };
  } else {
    if (key === 'showMore') {
      // show more button
      return {
        ...target,
        disabled: false,
      };
    }
    // group
    const { children } = target;
    if (Array.isArray(children)) {
      if (children.length === 0) {
        return {
          ...target,
          disabled: true,
        };
      } else {
        const newChildren = children.map((item) => convertTarget(item));
        return {
          ...target,
          disabled: !newChildren.every((item) => !item.disabled),
          children: newChildren,
        };
      }
    } else {
      return {
        ...target,
        disabled: true,
      };
    }
  }
}
export const availableTargetsSelector = (s: PolicyStore) => {
  const allTargets = s.availableTargetsGerneral;
  const result = allTargets.map((item) => {
    // disable the group node which contains no son node
    return convertTarget(item);
  });
  return result;
};
export const senderRecipientListDisabledSelector = (s: PolicyStore) => {
  const {
    tmVirusscans: {
      vsDisarmEnabled,
      vsNonOfficeDisarmEnabled,
      vsDisarmExceptEnabled,
    },
  } = s.template as ExchangeOnlinePolicyItem;
  return !(
    (vsDisarmEnabled || vsNonOfficeDisarmEnabled) &&
    vsDisarmExceptEnabled
  );
};
export const vsAdvancedOptionConfigSelector = (s: PolicyStore) => {
  const {
    config: {
      defaultValue: {
        tmVirusscans: { vsReplacementText: vsReplacementTextDefault },
      },
    },
  } = s;
  const {
    tmVirusscans: { vsReplacementFileName, vsReplacementText },
  } = s.template as ExchangeOnlinePolicyItem;
  const config = {
    replaceFileName: vsReplacementFileName,
    replaceText: vsReplacementText,
    replaceTextDefault: vsReplacementTextDefault,
  };
  return config;
};
export const vsAdvancedSettingsConfigSelector = (s: PolicyStore) => {
  const {
    config: {
      defaultValue: {
        tmVirusscans: { vsReplacementText: vsReplacementTextDefault },
      },
    },
  } = s;
  const {
    tmVirusscans: {
      vsSecondActionEnabled,
      vsSecondActionType,
      vsFileTagContent,
      vsReplacementText,
    },
  } = s.template as ExchangeOnlinePolicyItem;
  const config = {
    secondActionEnabled: vsSecondActionEnabled,
    secondActionType: vsSecondActionType,
    replaceFileTagContent: vsFileTagContent,
    replaceText: vsReplacementText,
    replaceTextDefault: vsReplacementTextDefault,
  };
  return config;
};
export const vsUnscannableFileConfigSelector = (s: PolicyStore) => {
  const {
    tmVirusscans: {
      vsReplacementFileNameUnscannable,
      vsReplacementTextUnscannable,
    },
  } = s.template as ExchangeOnlinePolicyItem;
  const pswProtectedFileAction = vsPswProtectedFileActionSelector(s);
  const config = {
    replaceFileName: vsReplacementFileNameUnscannable,
    replaceText: vsReplacementTextUnscannable,
    actionConfig: pswProtectedFileAction,
  };
  return config;
};
export const vsPswProtectedFileActionTeamsChatSelector = (s: PolicyStore) => {
  const {
    tmVirusscans: {
      vsEncryptedCompressedFileActionType,
      vsEncryptedCompressedFileNotifyType,
      vsOtherEncryptedFileActionType,
      vsOtherEncryptedFileNotifyType,
    },
  } = s.template as ExchangeOnlinePolicyItem;
  const { t } = i18nInst;
  const actionOptions = [
    ActionEnum.pass,
    ActionEnum.block,
    ActionEnum.passWithoutLogging,
  ];
  const actionData: ActionConfig[] = [
    {
      threatType: t('policy.threatType.passwordProtected'),
      action: vsEncryptedCompressedFileActionType,
      actionKey: VsActionKeys.vsEncryptedCompressedFileActionType,
      notifyType: vsEncryptedCompressedFileNotifyType,
      notifyTypeKey: VsNotifyTypeKeys.vsEncryptedCompressedFileNotifyType,
      actions: actionOptions,
    },
    {
      threatType: t('policy.threatType.otherPasswordProtected'),
      action: vsOtherEncryptedFileActionType,
      actionKey: VsActionKeys.vsOtherEncryptedFileActionType,
      notifyType: vsOtherEncryptedFileNotifyType,
      notifyTypeKey: VsNotifyTypeKeys.vsOtherEncryptedFileNotifyType,
      actions: actionOptions,
    },
  ];
  return actionData;
};

export const vsPswProtectedFileActionTeamsSelector = (s: PolicyStore) => {
  const {
    tmVirusscans: {
      vsEncryptedCompressedFileActionType,
      vsEncryptedCompressedFileNotifyType,
      vsOtherEncryptedFileActionType,
      vsOtherEncryptedFileNotifyType,
    },
  } = s.template as ExchangeOnlinePolicyItem;
  const { t } = i18nInst;
  const actionOptions = [
    ActionEnum.quarantine,
    ActionEnum.delete,
    ActionEnum.pass,
    ActionEnum.passWithoutLogging,
  ];
  const actionData: ActionConfig[] = [
    {
      threatType: t('policy.threatType.passwordProtected'),
      action: vsEncryptedCompressedFileActionType,
      actionKey: VsActionKeys.vsEncryptedCompressedFileActionType,
      notifyType: vsEncryptedCompressedFileNotifyType,
      notifyTypeKey: VsNotifyTypeKeys.vsEncryptedCompressedFileNotifyType,
      actions: actionOptions,
    },
    {
      threatType: t('policy.threatType.otherPasswordProtected'),
      action: vsOtherEncryptedFileActionType,
      actionKey: VsActionKeys.vsOtherEncryptedFileActionType,
      notifyType: vsOtherEncryptedFileNotifyType,
      notifyTypeKey: VsNotifyTypeKeys.vsOtherEncryptedFileNotifyType,
      actions: actionOptions,
    },
  ];
  return actionData;
};

export const vsPswProtectedFileActionGmailSelector = (s: PolicyStore) => {
  const {
    template,
    config: {
      featureShow: { gmailQtSettingFeatureShow },
    },
  } = s;
  const {
    tmVirusscans: {
      vsEncryptedCompressedFileActionType,
      vsEncryptedCompressedFileNotifyType,
      vsOtherEncryptedFileActionType,
      vsOtherEncryptedFileNotifyType,
    },
  } = template as ExchangeOnlinePolicyItem;
  const { t } = i18nInst;
  const actionOptions = gmailQtSettingFeatureShow
    ? [
        ActionEnum.delete,
        ActionEnum.pass,
        ActionEnum.quarantine,
        ActionEnum.labelEmail,
        ActionEnum.passWithoutLogging,
      ]
    : [
        ActionEnum.delete,
        ActionEnum.pass,
        ActionEnum.labelEmail,
        ActionEnum.passWithoutLogging,
      ];
  const actionData: ActionConfig[] = [
    {
      threatType: t('policy.threatType.passwordProtected'),
      action: vsEncryptedCompressedFileActionType,
      actionKey: VsActionKeys.vsEncryptedCompressedFileActionType,
      notifyType: vsEncryptedCompressedFileNotifyType,
      notifyTypeKey: VsNotifyTypeKeys.vsEncryptedCompressedFileNotifyType,
      actions: actionOptions,
    },
    {
      threatType: t('policy.threatType.otherPasswordProtected'),
      action: vsOtherEncryptedFileActionType,
      actionKey: VsActionKeys.vsOtherEncryptedFileActionType,
      notifyType: vsOtherEncryptedFileNotifyType,
      notifyTypeKey: VsNotifyTypeKeys.vsOtherEncryptedFileNotifyType,
      actions: actionOptions,
    },
  ];
  return actionData;
};
